import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { proposalV2API } from '../utils/api';
import {
  FileText, Plus, Download, Eye, ChevronRight, Sparkles, CheckCircle2,
  Building2, MapPin, Zap, Fuel, ArrowRight, Wand2, FileBarChart, FilePlus2,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Defaults used when starting blank ──────────────────────────
const DEFAULT_PAYLOAD = {
  customer: { name: '', site_name: '', address: '', poc: '' },
  system: {
    pcs_kva: 250, energy_kwh: 500, backup_hours: 4,
    specs: { chemistry: 'LFP (LiFePO4)', voltage_range: '614–900 V DC', temp_range: '-10°C to 50°C', cycle_life: '6,000+ cycles @ 80% DoD', communication: 'Modbus TCP / CAN' }
  },
  purchase: { capex_exgst_inr: 10000000, installation_exgst_inr: 500000, terms: ['50% advance on order confirmation', '40% on delivery to site', '10% on commissioning'] },
  rental: { capex_exgst_inr: 10000000, irr: 0.14, tenor_years: 7, billable_pct: 0.70 },
  diesel_log: { period_months: 6, total_litres: 12000, total_bill_inr: 1092000 },
  assumptions: { dg_kwh_per_litre: 3.4, eta_delivered: 0.95, rte: 0.95, charging_tariff: 8.5, gst: 0.18, amc_pct: 0.03, co2_kg_per_litre: 2.68, depr_rate: 0.15, tax_rate: 0.25 },
};

// ── Template metadata (user-facing descriptions) ───────────────
const TEMPLATE_META = {
  'voltide.sale_only.v1': {
    icon: FilePlus2,
    tagline: 'Outright purchase (CapEx)',
    description: 'Straight sale proposal with capex pricing, commercial terms, and ROI vs diesel.',
    color: 'blue',
  },
  'voltide.sale_or_rental.v1': {
    icon: FileBarChart,
    tagline: 'Dual option — client chooses',
    description: 'Present both Sale and Rental (BaaS) side-by-side so the client picks the best fit.',
    color: 'violet',
  },
  'voltide.rental_only.v1': {
    icon: FileText,
    tagline: 'Battery-as-a-Service (OpEx)',
    description: 'Monthly rental proposal with tenor, monthly EMI, and billable-hour commitments.',
    color: 'emerald',
  },
};

const COLOR_MAP = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'hover:border-blue-300', btn: 'bg-blue-600 hover:bg-blue-700' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'hover:border-violet-300', btn: 'bg-violet-600 hover:bg-violet-700' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'hover:border-emerald-300', btn: 'bg-emerald-600 hover:bg-emerald-700' },
};

// ═══════════════════════════════════════════════════════════════
// MAIN — Template Library / Proposals List
// ═══════════════════════════════════════════════════════════════
export default function ProposalBuilderV2Page() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('templates');
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [autofillTemplate, setAutofillTemplate] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, pRes] = await Promise.all([proposalV2API.listTemplates(), proposalV2API.list()]);
        setTemplates(tRes.data || []);
        setProposals(pRes.data || []);
      } catch (e) { console.error('ProposalBuilderV2Page load failed', e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const openAutofill = (templateId) => {
    setAutofillTemplate(templateId);
    setAutofillOpen(true);
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>;

  return (
    <div className="flex min-h-screen bg-slate-50 overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 overflow-x-hidden">
        <div className="p-4 sm:p-8 space-y-6 max-w-7xl">
          <PageHeader title="Proposals" description="Create consulting-style BESS proposals — auto-filled from prospect data or start fresh." />

          {/* Tabs */}
          <div className="flex space-x-1 bg-slate-100 rounded-sm p-0.5 w-fit">
            {[{ k: 'templates', l: 'New Proposal' }, { k: 'proposals', l: `My Proposals (${proposals.length})` }].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`px-4 py-1.5 text-xs font-medium rounded-sm transition ${tab === t.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {tab === 'templates' && (
            <>
              {/* Workflow Hero */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-sm p-6 text-white" data-testid="workflow-hero">
                <div className="flex items-center space-x-2 mb-3">
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span className="text-[11px] uppercase tracking-wide text-amber-300 font-semibold">Fastest way to a proposal</span>
                </div>
                <h2 className="text-lg font-bold mb-1">One click → Done</h2>
                <p className="text-xs text-slate-300 mb-4">
                  Select a prospect site — we'll pull customer details, configurator sizing, and diesel log data into the template automatically.
                </p>
                <div className="flex items-center space-x-3 text-[11px] text-slate-400">
                  <Step n={1} label="Pick template & site" active />
                  <ArrowRight className="w-3 h-3" />
                  <Step n={2} label="Review auto-filled data" />
                  <ArrowRight className="w-3 h-3" />
                  <Step n={3} label="Preview & export PDF" />
                </div>
              </div>

              {/* Templates Grid */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Choose a template</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="template-grid">
                  {templates.map(t => {
                    const meta = TEMPLATE_META[t.template_id] || { icon: FileText, tagline: '', description: '', color: 'blue' };
                    const Icon = meta.icon;
                    const c = COLOR_MAP[meta.color];
                    return (
                      <div key={t.template_id}
                        className={`bg-white border border-slate-200 rounded-sm p-5 transition-colors ${c.border}`}
                        data-testid={`template-${t.template_id}`}>
                        <div className={`w-11 h-11 ${c.bg} rounded-sm flex items-center justify-center mb-3`}>
                          <Icon className={`w-5 h-5 ${c.text}`} />
                        </div>
                        <h4 className="text-sm font-semibold text-slate-900">{t.name}</h4>
                        <p className={`text-[10px] ${c.text} font-medium uppercase tracking-wide mt-0.5`}>{meta.tagline}</p>
                        <p className="text-xs text-slate-500 mt-2 leading-relaxed min-h-[48px]">{meta.description}</p>
                        <div className="space-y-2 mt-4">
                          <Button size="sm" className={`w-full ${c.btn} text-xs`}
                            onClick={() => openAutofill(t.template_id)}
                            data-testid={`autofill-from-${t.template_id}`}>
                            <Wand2 className="w-3 h-3 mr-1.5" /> Generate from Prospect
                          </Button>
                          <Button size="sm" variant="outline" className="w-full text-xs border-slate-200"
                            onClick={() => navigate(`/proposals/new?template=${t.template_id}`)}
                            data-testid={`create-from-${t.template_id}`}>
                            <Plus className="w-3 h-3 mr-1.5" /> Start Blank
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {tab === 'proposals' && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="proposals-list">
              {proposals.length === 0 ? (
                <div className="p-16 text-center">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-600 font-medium mb-1">No proposals yet</p>
                  <p className="text-xs text-slate-400 mb-5">Head to <span className="font-semibold text-slate-600">New Proposal</span> to create your first one.</p>
                  <Button size="sm" className="text-xs bg-slate-900 hover:bg-slate-800" onClick={() => setTab('templates')} data-testid="goto-templates-btn">
                    Create Proposal <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {proposals.map(p => (
                    <div key={p.proposal_id} className="px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => navigate(`/proposals/${p.proposal_id}`)} data-testid={`proposal-row-${p.proposal_id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{p.payload?.customer?.name || 'Unnamed'} <span className="text-slate-400">—</span> {p.payload?.customer?.site_name || ''}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{p.template_id.split('.')[1]} · {new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${p.status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>{p.status}</span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AutofillDialog
        open={autofillOpen}
        onOpenChange={setAutofillOpen}
        templateId={autofillTemplate}
        onCreated={(proposalId) => { setAutofillOpen(false); navigate(`/proposals/${proposalId}`); }}
      />
    </div>
  );
}

// ── Small step pill for the hero ───────────────────────────────
function Step({ n, label, active }) {
  return (
    <span className={`inline-flex items-center space-x-1.5 ${active ? 'text-white' : ''}`}>
      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${active ? 'bg-amber-300 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>{n}</span>
      <span>{label}</span>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUTOFILL DIALOG — Pick a prospect site, one-click generate
// ═══════════════════════════════════════════════════════════════
function AutofillDialog({ open, onOpenChange, templateId, onCreated }) {
  const navigate = useNavigate();
  const onClose = () => onOpenChange(false);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    proposalV2API.listEligibleSites()
      .then(res => setSites(res.data || []))
      .catch(e => toast.error(e.userMessage || 'Failed to load prospect sites'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleGenerate = async (siteId) => {
    setCreating(siteId);
    try {
      const res = await proposalV2API.createFromSite(templateId, siteId);
      toast.success('Proposal auto-generated from prospect data');
      onCreated(res.data.proposal_id);
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Auto-generate failed');
    } finally {
      setCreating(null);
    }
  };

  const filtered = sites.filter(s =>
    !search ||
    s.company_name.toLowerCase().includes(search.toLowerCase()) ||
    s.site_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="autofill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Wand2 className="w-4 h-4 text-amber-500" />
            <span>Generate from Prospect Site</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            We'll pull customer details, configurator sizing, and diesel logs into the proposal.
          </DialogDescription>
        </DialogHeader>

        <input
          type="text"
          placeholder="Search by company or site..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-slate-200 rounded px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid="autofill-search"
        />

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="py-10 text-center text-xs text-slate-400">Loading prospect sites…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center" data-testid="autofill-empty">
              <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-xs text-slate-500">
                {sites.length === 0
                  ? 'No prospect sites found yet. Add a prospect with at least one site, then return here.'
                  : 'No sites match your search.'}
              </p>
              {sites.length === 0 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => { onClose(); navigate('/sales/prospects'); }} data-testid="autofill-goto-prospects">
                    Go to Prospects
                  </Button>
                  <Button size="sm" onClick={() => { onClose(); navigate('/sales/prospects?new=1'); }} data-testid="autofill-create-prospect">
                    Create Prospect
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-3">
                You can also <button onClick={onClose} className="underline" data-testid="autofill-start-blank">start with a blank proposal</button> instead.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map(s => {
                const readiness = (s.has_configuration ? 1 : 0) + (s.has_diesel_data ? 1 : 0);
                return (
                  <div key={s.prospect_site_id} className="py-3 flex items-start justify-between gap-3" data-testid={`autofill-site-${s.prospect_site_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <p className="text-sm font-medium text-slate-900 truncate">{s.company_name}</p>
                      </div>
                      <div className="flex items-center space-x-2 mt-1 text-[11px] text-slate-500">
                        <MapPin className="w-3 h-3" /> <span className="truncate">{s.site_name} · {s.address || 'No address'}</span>
                      </div>
                      <div className="flex items-center space-x-3 mt-2">
                        <Badge ok={s.has_configuration} icon={Zap} label="Config" />
                        <Badge ok={s.has_diesel_data} icon={Fuel} label={`DG logs${s.has_diesel_data ? ` (${s.dg_log_count})` : ''}`} />
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className={`text-xs flex-shrink-0 ${readiness === 2 ? 'bg-emerald-600 hover:bg-emerald-700' : readiness === 1 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-500 hover:bg-slate-600'}`}
                      disabled={creating === s.prospect_site_id}
                      onClick={() => handleGenerate(s.prospect_site_id)}
                      data-testid={`generate-from-${s.prospect_site_id}`}
                    >
                      {creating === s.prospect_site_id ? 'Generating…' : 'Generate'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="text-[10px] text-slate-400">
          Sites with both Config + DG logs produce the most accurate proposals.
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Badge({ ok, icon: Icon, label }) {
  return (
    <span className={`inline-flex items-center space-x-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
      {ok ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Icon className="w-2.5 h-2.5" />}
      <span>{label}</span>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// EDITOR — Payload Form / Preview / Computed Fields
// ═══════════════════════════════════════════════════════════════
export function ProposalEditorV2Page() {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const isNew = proposalId === 'new';
  const templateIdFromQuery = searchParams.get('template');

  const [proposal, setProposal] = useState(null);
  const [payload, setPayload] = useState(DEFAULT_PAYLOAD);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [activeTab, setActiveTab] = useState('form');

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      try {
        if (isNew && templateIdFromQuery) {
          const tRes = await proposalV2API.getTemplate(templateIdFromQuery);
          if (!ignore) setTemplate(tRes.data);
        } else if (!isNew && proposalId) {
          const pRes = await proposalV2API.get(proposalId);
          if (ignore) return;
          setProposal(pRes.data);
          setPayload(pRes.data.payload || DEFAULT_PAYLOAD);
          const tRes = await proposalV2API.getTemplate(pRes.data.template_id);
          if (!ignore) setTemplate(tRes.data);
        }
      } catch (e) {
        if (!ignore) toast.error(e.response?.data?.detail || 'Failed to load');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => { ignore = true; };
  }, [proposalId, isNew, templateIdFromQuery]);

  const updateField = (path, value) => {
    setPayload(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!cur[parts[i]]) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await proposalV2API.create(templateIdFromQuery, payload);
        toast.success('Proposal created');
        navigate(`/proposals/${res.data.proposal_id}`, { replace: true });
      } else {
        const res = await proposalV2API.update(proposalId, payload);
        // Reload proposal to get fresh derived fields
        const pRes = await proposalV2API.get(proposalId);
        setProposal(pRes.data);
        toast.success('Saved');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handlePreview = async () => {
    setActiveTab('preview');
    if (!proposal) { toast.error('Save the proposal first'); return; }
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/v2/proposals/${proposal.proposal_id}/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const html = await res.text();
      setPreviewHtml(html);
    } catch { toast.error('Preview failed'); }
  };

  const handlePdf = async () => {
    if (!proposal) { toast.error('Save the proposal first'); return; }
    setRendering(true);
    try {
      const res = await proposalV2API.renderPdf(proposal.proposal_id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Voltide_Proposal_${proposal.proposal_id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('PDF generation failed'); }
    finally { setRendering(false); }
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>;

  const autofilledFrom = proposal?.autofilled_from || {};
  const stepNum = isNew ? 1 : (previewHtml ? 3 : 2);

  return (
    <div className="flex min-h-screen bg-slate-50 overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 overflow-x-hidden">
        <div className="p-4 sm:p-8 space-y-4 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center space-x-2">
                <button onClick={() => navigate('/proposals')} className="text-xs text-slate-400 hover:text-slate-700" data-testid="breadcrumb-back">
                  Proposals
                </button>
                <ChevronRight className="w-3 h-3 text-slate-300" />
                <span className="text-xs text-slate-700">{isNew ? 'New' : proposal?.payload?.customer?.name || 'Untitled'}</span>
              </div>
              <h1 className="text-lg font-bold text-slate-900 mt-1">{isNew ? 'New Proposal' : (proposal?.payload?.customer?.name || 'Edit Proposal')}</h1>
              <p className="text-xs text-slate-500">{template?.name} · v{template?.version}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/proposals')}>Back</Button>
              <Button size="sm" className="text-xs bg-slate-900 hover:bg-slate-800" onClick={handleSave} disabled={saving} data-testid="save-proposal-btn">
                {saving ? 'Saving…' : (isNew ? 'Save & Continue' : 'Save')}
              </Button>
              {!isNew && (
                <>
                  <Button variant="outline" size="sm" className="text-xs" onClick={handlePreview} data-testid="preview-btn">
                    <Eye className="w-3 h-3 mr-1" /> Preview
                  </Button>
                  <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700" onClick={handlePdf} disabled={rendering} data-testid="pdf-btn">
                    <Download className="w-3 h-3 mr-1" /> {rendering ? 'Generating…' : 'PDF'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Step indicator */}
          <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center text-[11px]" data-testid="step-indicator">
            <StepPill n={1} label="Fill details" active={stepNum === 1} done={stepNum > 1} />
            <span className="flex-1 h-px bg-slate-200 mx-2" />
            <StepPill n={2} label="Preview" active={stepNum === 2} done={stepNum > 2} />
            <span className="flex-1 h-px bg-slate-200 mx-2" />
            <StepPill n={3} label="Export PDF" active={stepNum === 3} done={false} />
          </div>

          {/* Autofill banner */}
          {proposal?.source_site_id && Object.keys(autofilledFrom).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center space-x-2 text-xs" data-testid="autofill-banner">
              <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-amber-900">
                <strong>Auto-filled from prospect site</strong> — review and adjust the following sections:{' '}
                {Object.keys(autofilledFrom).map(k => k.replace('_', ' ')).join(', ')}. Commercial terms use defaults — edit them below.
              </span>
            </div>
          )}

          {/* Tabs */}
          <div className="flex space-x-1 bg-slate-100 rounded-sm p-0.5 w-fit">
            {[{ k: 'form', l: 'Payload Form' }, { k: 'preview', l: 'Preview' }, { k: 'derived', l: 'Computed Fields' }].map(t => (
              <button key={t.k} onClick={() => t.k === 'preview' ? handlePreview() : setActiveTab(t.k)}
                className={`px-4 py-1.5 text-xs font-medium rounded-sm transition ${activeTab === t.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid={`editor-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {/* Form Tab */}
          {activeTab === 'form' && (
            <div className="space-y-4" data-testid="payload-form">
              <FormSection title="Customer" autofilled={autofilledFrom.customer} fields={[
                { path: 'customer.name', label: 'Company Name', type: 'text' },
                { path: 'customer.site_name', label: 'Site Name', type: 'text' },
                { path: 'customer.address', label: 'Address', type: 'text' },
                { path: 'customer.poc', label: 'Point of Contact', type: 'text' },
              ]} payload={payload} onChange={updateField} />

              <FormSection title="System Sizing" autofilled={autofilledFrom.system} fields={[
                { path: 'system.pcs_kva', label: 'PCS Rating (kVA)', type: 'number' },
                { path: 'system.energy_kwh', label: 'Battery Energy (kWh)', type: 'number' },
                { path: 'system.backup_hours', label: 'Backup Hours', type: 'number' },
              ]} payload={payload} onChange={updateField} />

              <FormSection title="Technical Specs" fields={[
                { path: 'system.specs.chemistry', label: 'Chemistry', type: 'text' },
                { path: 'system.specs.voltage_range', label: 'Voltage Range', type: 'text' },
                { path: 'system.specs.temp_range', label: 'Temp Range', type: 'text' },
                { path: 'system.specs.cycle_life', label: 'Cycle Life', type: 'text' },
                { path: 'system.specs.communication', label: 'Communication', type: 'text' },
              ]} payload={payload} onChange={updateField} />

              <FormSection title="Purchase (Sale)" fields={[
                { path: 'purchase.capex_exgst_inr', label: 'BESS Capex (ex-GST, INR)', type: 'number' },
                { path: 'purchase.installation_exgst_inr', label: 'Installation (ex-GST, INR)', type: 'number' },
              ]} payload={payload} onChange={updateField} />

              <FormSection title="Rental Inputs" fields={[
                { path: 'rental.capex_exgst_inr', label: 'Capex (ex-GST, INR)', type: 'number' },
                { path: 'rental.irr', label: 'Target IRR (internal-only — not shown in proposal)', type: 'number', step: '0.01' },
                { path: 'rental.tenor_years', label: 'Tenor (years)', type: 'number' },
                { path: 'rental.billable_pct', label: 'Billable % (decimal, e.g., 0.70)', type: 'number', step: '0.01' },
              ]} payload={payload} onChange={updateField} />

              <FormSection title="Diesel Log Summary" autofilled={autofilledFrom.diesel_log} fields={[
                { path: 'diesel_log.period_months', label: 'Period (months)', type: 'number' },
                { path: 'diesel_log.total_litres', label: 'Total Litres', type: 'number' },
                { path: 'diesel_log.total_bill_inr', label: 'Total Bill (INR)', type: 'number' },
              ]} payload={payload} onChange={updateField} />

              <FormSection title="Assumptions" fields={[
                { path: 'assumptions.dg_kwh_per_litre', label: 'DG kWh/litre', type: 'number', step: '0.1' },
                { path: 'assumptions.eta_delivered', label: 'Efficiency (eta)', type: 'number', step: '0.01' },
                { path: 'assumptions.rte', label: 'Round-Trip Eff.', type: 'number', step: '0.01' },
                { path: 'assumptions.charging_tariff', label: 'Charging Tariff (INR/kWh)', type: 'number', step: '0.5' },
                { path: 'assumptions.gst', label: 'GST (decimal)', type: 'number', step: '0.01' },
                { path: 'assumptions.amc_pct', label: 'AMC % (decimal)', type: 'number', step: '0.01' },
                { path: 'assumptions.co2_kg_per_litre', label: 'CO₂ kg/litre diesel', type: 'number', step: '0.01' },
              ]} payload={payload} onChange={updateField} />
            </div>
          )}

          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="preview-container">
              {previewHtml ? (
                <iframe srcDoc={previewHtml} title="Proposal Preview" className="w-full" style={{ height: '80vh', border: 'none' }} />
              ) : (
                <div className="p-12 text-center">
                  <Eye className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500">Save the proposal first, then click Preview.</p>
                </div>
              )}
            </div>
          )}

          {/* Derived Fields Tab */}
          {activeTab === 'derived' && proposal?.derived && (
            <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="derived-fields">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Computed Fields</h3>
              <p className="text-xs text-slate-500 mb-4">Values derived from your payload via template formulas. Update the form and save to recompute.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(proposal.derived).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-xs">
                    <span className="text-slate-600 font-mono">{k}</span>
                    <span className="text-slate-900 font-semibold font-mono">{typeof v === 'number' ? v.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── UI atoms ───────────────────────────────────────────────────
function StepPill({ n, label, active, done }) {
  const cls = done ? 'bg-emerald-100 text-emerald-700' : active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400';
  return (
    <span className={`inline-flex items-center space-x-1.5 px-2 py-1 rounded-sm ${cls}`}>
      <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[9px] font-bold">
        {done ? <CheckCircle2 className="w-3 h-3" /> : n}
      </span>
      <span className="font-medium">{label}</span>
    </span>
  );
}

function FormSection({ title, fields, payload, onChange, autofilled }) {
  const getVal = (path) => {
    const parts = path.split('.');
    let cur = payload;
    for (const p of parts) { cur = cur?.[p]; }
    return cur ?? '';
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">{title}</h3>
        {autofilled && (
          <span className="inline-flex items-center space-x-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded" data-testid={`autofilled-${title.toLowerCase().replace(/ /g, '-')}`}>
            <Sparkles className="w-2.5 h-2.5" /> <span>Auto-filled</span>
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {fields.map(f => (
          <div key={f.path}>
            <label className="text-[10px] text-slate-500 block mb-1">{f.label}</label>
            <input
              type={f.type || 'text'}
              step={f.step}
              value={getVal(f.path)}
              onChange={e => onChange(f.path, f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
              className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid={`field-${f.path}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
